// NPM Imports
import moment from "moment";
import amplitude from "amplitude-js";

// Internal Imports
import { AUTH, ERROR_MSG, SETTINGS, EVENTS, PATHS, ASSET } from "../constants";
import store from "../store";

// Destructuring
const { API_ERRORS } = ERROR_MSG;
const { FREE_TRAIL } = SETTINGS;
const { LOCAL, DOMAIN_NAME } = AUTH;
const { ASSET_PAGE_URL } = ASSET;

// Declarations
let gridApi = null;
const intRegex = /^[0-9\b]+$/;

/*
==========================================
         Value Mutation Functions
==========================================
*/

export const numberForamting = (num, prefix = "", suffix = "") => {
  if (num) {
    let number;
    if (intRegex.test(num)) {
      const test = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      number = `${test}.00`;
    } else {
      const n = num.toString();
      const parts = (+n).toFixed(2).split(".");
      number =
        parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
        (+parts[1] ? `.${parts[1]}` : "");
    }
    return `${prefix}${number}${suffix}`;
  }
  return `${prefix}0${suffix}`;
};

export const formatDate = (date, momentFormat) =>
  moment(date).format(momentFormat);

export function addValueAndLabelPropsForSelectOptions(
  array = [],
  propToBeCopied,
) {
  // console.debug(array);
  // React Select Library needs a value and a label property on each option
  // This function will create these 2 props on each option by duplicating the value of prop passed as 'propToBeCopied' - eg - value: item.name, label: item.name
  return array.map(item => ({
    ...item,
    value: item[propToBeCopied],
    label: item[propToBeCopied],
  }));
}

export const camelCaseToSpace = name => name.replace(/([A-Z])/g, " $1");

export const roundOffNumber = num => +`${Math.round(`${num}e+2`)}e-2`;

export const addPriceComma = num =>
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/*
==========================================
     Storage Related Functions
==========================================
*/

export const readCookie = name => {
  const cookieName = `${name}=`;
  const cookies = document.cookie.split(";");
  // eslint-disable-next-line no-restricted-syntax
  for (const key of cookies.keys()) {
    let cookie = cookies[key];
    while (cookie.charAt(0) === " ")
      cookie = cookie.substring(1, cookie.length);
    if (cookie.indexOf(cookieName) === 0)
      return cookie.substring(cookieName.length, cookie.length);
  }
  return null;
};

export const setCookie = (name, value) => {
  const envName = process.env.REACT_APP_ENVIRONMENT;
  if (envName === LOCAL) {
    document.cookie = `${name} = ${value}; path=/; domain=localhost`;
  } else {
    const domain = DOMAIN_NAME[envName];
    if (domain) {
      document.cookie = `${name} = ${value}; path=/; domain=${domain}`;
    }
  }
};

export const removeCookie = name => {
  const envName = process.env.REACT_APP_ENVIRONMENT;
  if (envName === LOCAL) {
    document.cookie = `${name} =; path=/; domain=localhost; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  } else {
    const domain = DOMAIN_NAME[envName];
    if (domain) {
      document.cookie = `${name} =; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    }
  }
};

export const setSessionStorage = (name, value) => {
  sessionStorage.setItem(name, value);
};

export const getSessionStorage = name => {
  sessionStorage.getItem(name);
};

export const writeDataToLocalStorage = (key, value) => {
  localStorage.removeItem(key);
  localStorage.setItem(key, JSON.stringify(value));
};

export const readDataFromLocalStorage = key =>
  localStorage.getItem(key) ? JSON.parse(localStorage.getItem(key)) : undefined;

export const removeDataFromLocalStorage = key => {
  localStorage.removeItem(key);
};

export const checkLocalStorageForUserObject = () => {
  if (localStorage.getItem("revvsalesUser") === null) {
    return false;
  }
  store.dispatch({ type: "CHANGE_AUTH", payload: true });
  return true;
};

/*
==========================================
     API Error Msg Function
==========================================
*/

export const handleApiErrMsg = err => {
  if (err && err.response && err.response.data) {
    return (
      API_ERRORS[err.response.data.code] ||
      err.response.data.message ||
      API_ERRORS.DEFAULT_MSG
    );
  }
  return API_ERRORS.DEFAULT_MSG;
};

export const handleErrorMessage = error => {
  // if error is just a message
  if (typeof error === "string") {
    return error;
  }

  // if it is an instance of Error Object, just return message
  if (error instanceof Error) {
    return error.message;
  }

  // if it is a instance of response error
  const res = error.response;
  let message = "";
  if (res === undefined) {
    message = "There is some issue with network. Please re-try after sometime.";
  } else if (typeof res === "object") {
    const { status } = res;
    if (parseInt(status, 10) === 404) {
      message = "Requested document is not available.";
    } else if (parseInt(status, 10) === 400) {
      message =
        "Unable to fetch the requested document. Please re-try after sometime.";
    } else if (parseInt(status, 10) === 500) {
      message = "Facing backend issue. Please re-try after sometime.";
    }
  }
  return message;
};

/*
==========================================
              RBAC Functions
==========================================
*/

export const arrayHasObjectWithKeyAndVal = (array, objKey, objVal) => {
  let has = false;
  if (array) {
    for (let index = 0; index < array.length; index += 1) {
      const element = array[index];
      if (
        Object.prototype.hasOwnProperty.call(element, objKey) &&
        element[objKey] === objVal
      ) {
        has = true;
        break;
      }
    }
  }

  return has;
};

export const applyRbac = (views, permissions) => {
  const newViews = views.filter(view => {
    if (Object.prototype.hasOwnProperty.call(view, "readPermission")) {
      if (
        arrayHasObjectWithKeyAndVal(permissions, "name", view.readPermission)
      ) {
        return true;
      }
      return false;
    }
    return true;
  });
  return newViews;
};

/*
==========================================
   Products used & unused check Function
==========================================
*/
// this method looks for a particular object array1 if fails looks into array2
export const twoArrayLookUp = (array1, array2, matchID) => {
  let obj = array1.find(item => item.id === matchID);
  if (!obj) {
    obj = array2.find(item => item.id === matchID);
  }
  return obj || {};
};

/*
==========================================
         AG-Grid Functions
==========================================
*/
export const saveGridAPi = api => {
  gridApi = api;
};

export const getGridAPi = () => gridApi;

export const saveGridState = page => {
  // Do the change only when grid api's are avialble
  if (gridApi) {
    const obj = {};
    obj.colState = gridApi.columnApi.getColumnState();
    obj.groupState = gridApi.columnApi.getColumnGroupState();
    obj.sortState = gridApi.api.getSortModel();
    obj.filterState = gridApi.api.getFilterModel();
    writeDataToLocalStorage(page, JSON.stringify(obj));
  }
};

export const restoreGridState = page => {
  // Do the change only when grid api's are avialble
  if (gridApi) {
    const obj = JSON.parse(readDataFromLocalStorage(page));
    gridApi.columnApi.setColumnState(obj.colState);
    gridApi.columnApi.setColumnGroupState(obj.groupState);
    gridApi.api.setSortModel(obj.sortState);
    gridApi.api.setFilterModel(obj.filterState);
  }
};

// Copy to Clipboard
export const copyToClipBoard = data => {
  try {
    const inp = document.createElement("input");
    document.body.appendChild(inp);
    inp.value = data;
    inp.select();
    document.execCommand("copy");
    document.body.removeChild(inp);
    return true;
  } catch (err) {
    return false;
  }
};

export const checkUserPermission = permissionName => {
  const userPermissions = readDataFromLocalStorage("revvsalesUserPermissions");
  return (
    userPermissions &&
    userPermissions.find(permission => permission.name === permissionName)
  );
};

// Empty Object Check
export const isEmptyObj = obj =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

export const priceConverter = price => {
  if (price) {
    const value = Math.abs(Number(price));
    // Nine Zeroes for Billions
    if (value >= 1.0e9) {
      return `${(value / 1.0e9).toFixed(2)}B`;
    }
    // Six Zeroes for Millions
    if (value >= 1.0e6) {
      return `${(value / 1.0e6).toFixed(2)}M`;
    }
    // Three Zeroes for Thousands
    if (value >= 1.0e3) {
      return `${(value / 1.0e3).toFixed(2)}K`;
    }
    // else return the same value
    return value;
  }
  return price;
};

/*
==========================================
         Org Based Flag Function
==========================================
*/

export const checkAppFlag = flagName => {
  const appFlag = readDataFromLocalStorage("appFlag") || {};
  const flag = (appFlag && appFlag[flagName]) || "";
  return flag === "off";
};

export const getAppFlagValue = flagName => {
  const appFlag = readDataFromLocalStorage("appFlag") || {};
  const flag = (appFlag && appFlag[flagName]) || "";
  return flag;
};

// Validate email
export const validateEmail = email => {
  // eslint-disable-next-line no-useless-escape
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+[a-zA-Z\-0-9]+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(
    String(email)
      .toLowerCase()
      .trim(),
  );
};

// check the if billing plan if free
export const isFreeTrailBillingPlan = () => {
  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  if (CURRENT_USER) {
    if (
      CURRENT_USER.billing_plan === FREE_TRAIL ||
      CURRENT_USER.billing_plan === ""
    ) {
      return true;
    }
    return false;
  }
  return false;
};

/*
  check if two is equal
  */
export const equalArray = (array1, array2) => {
  if (!Array.isArray(array1) || !Array.isArray(array2)) return false;

  if (array1 === array2) return true;

  if (array1.length !== array2.length) return false;

  for (let i = 0; i < array1.length; i += 1) {
    if (Array.isArray(array1[i]) && Array.isArray(array2[i])) {
      const equals = equalArray(array1[i], array2[i]);
      if (!equals) return false;
    } else if (typeof array1[i] !== typeof array2[i]) {
      return false;
    } else if (array1[i] instanceof Object) {
      if (!equalArray(Object.keys(array1[i]), Object.keys(array2[i])))
        return false;
    } else if (array1[i] !== array2[i]) {
      return false;
    }
  }
  return true;
};

export const mapPageNameToEvent = page => {
  switch (page) {
    case PATHS.PAGES.PIPELINE.name:
      return EVENTS.EVENT_NAMES.PIPELINE_PAGE_VIEWED;
    case PATHS.PAGES.TEMPLATES.name:
      return EVENTS.EVENT_NAMES.TEMPLATES_PAGE_VIEWED;
    case PATHS.PAGES.DASHBOARD.name:
      return EVENTS.EVENT_NAMES.DASHBOARD_PAGE_VIEWED;
    case PATHS.PAGES.PRODUCTS.name:
      return EVENTS.EVENT_NAMES.PRODUCT_PAGE_VIEWED;
    case PATHS.PAGES.PRICEBOOKS.name:
      return EVENTS.EVENT_NAMES.PRICEBOOK_PAGE_VIEWED;
    case PATHS.PAGES.ASSETS.name:
      return EVENTS.EVENT_NAMES.ASSETS_PAGE_VIEWED;
    case PATHS.PAGES.WORKFLOWS.name:
      return EVENTS.EVENT_NAMES.WORKFLOW_PAGE_VIEWED;
    case PATHS.PAGES.INBOX.name:
      return EVENTS.EVENT_NAMES.INBOX_PAGE_VIEWED;
    default:
      return "";
  }
};

export const pageLoadSetup = page => {
  // set page title
  document.title = page.title;
  // call amplitude
  amplitude.getInstance().logEvent(mapPageNameToEvent(page.name));
};

export const getAssetFolderUrl = (folderName, folderId) => {
  if (folderId === null) {
    return `/${ASSET_PAGE_URL}`;
  }
  return `/${ASSET_PAGE_URL}/${folderName
    .toLocaleLowerCase()
    .split(" ")
    .join("-")}/${folderId}`;
};

export const truncateString = (name, size, flag, dot) => {
  if (name) {
    let nosDot = "...";
    if (dot) {
      nosDot = "";
      for (let i = 0; i < dot; i += 1) {
        nosDot += ".";
      }
    }
    if (name.length > size) {
      let value = name.slice(0, size);
      if (flag) {
        if (name.length >= size) {
          value += nosDot;
        }
      }
      return value;
    }
    return name;
  }
  return null;
};

export const revvDebounce = (func, threshold, execAsap) => {
  let timeoutId; // handle to setTimeout async task (detection period)

  // eslint-disable-next-line func-names
  return function debounced() {
    const obj = this;

    // eslint-disable-next-line prefer-rest-params
    const args = arguments; // arguments at execution time
    // this is the function which will be executed if/when the threshold expires
    const functionCall = () => {
      // if we're executing at the end of the detection period
      if (!execAsap) {
        func.apply(obj, args); // execute now
      }
      timeoutId = null; // clear timeout handle
    };
    // stop any current detection period
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // otherwise, if we're not already waiting and we're executing at the beginning of the detection period
    else if (execAsap) {
      func.apply(obj, args);
    }

    timeoutId = setTimeout(functionCall, threshold || 100);
  };
};

// Throttle function: Input as function which needs to be throttled and
//  delay is the time interval in milliseconds
export const revvThrottle = (fun, limit) => {
  let timeoutId = false; // Initially, we're not waiting
  return function() {
    // We return a throttled function
    if (!timeoutId) {
      const context = this;
      // eslint-disable-next-line prefer-rest-params
      const args = arguments; // arguments at execution time
      // If we're not waiting
      fun.apply(context, args); // Execute users function
      timeoutId = true; // Prevent future invocations
      setTimeout(() => {
        // After a period of time
        timeoutId = false; // And allow future invocations
      }, limit);
    }
  };
};

// Detect if the user is using safari browrser
export const isSafariBrowser = () =>
  navigator.userAgent.indexOf("Safari") !== -1 &&
  navigator.userAgent.indexOf("Chrome") === -1;

// Detect if the user is using chrome
export const isChromeBrowser = () => {
  const isChrome = navigator.userAgent.indexOf("Chrome") >= 0;
  const isEdge = navigator.userAgent.indexOf("Edg") >= 0;
  const isSafari = isSafariBrowser();
  const isFirefox = navigator.userAgent.indexOf("Firefox") >= 0;

  return isChrome && !isEdge && !isSafari && !isFirefox;
};

export const isMobileBrowser = () =>
  navigator.userAgent.match(/Android/i) ||
  navigator.userAgent.match(/webOS/i) ||
  navigator.userAgent.match(/iPhone/i) ||
  navigator.userAgent.match(/iPad/i) ||
  navigator.userAgent.match(/iPod/i) ||
  navigator.userAgent.match(/BlackBerry/i) ||
  navigator.userAgent.match(/Windows Phone/i);

export const isMobileAndroidBrowser = () =>
  navigator.userAgent.match(/Android/i) || isChromeBrowser();



// WEBPACK FOOTER //
// ./src/utils/globalUtils.js